import { useEffect, useState } from 'react';

function getMediaQueryMatches(query: string, fallback: boolean): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return fallback;
  }
  return window.matchMedia(query).matches;
}

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 * Initial-render safe: returns `false` when `window`/`matchMedia` is
 * unavailable, then syncs on mount and on every change.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => getMediaQueryMatches(query, false));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    // Sync immediately in case the query changed since the initial state.
    setMatches(mql.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Tailwind `lg` breakpoint (1024px) — matches the app's nav sidebar boundary. */
const LG_BREAKPOINT = 1024;
const DESKTOP_MEDIA_QUERY = `(min-width: ${LG_BREAKPOINT}px)`;
const MOBILE_MEDIA_QUERY = `(max-width: 40rem)`; // TODO: rename

/** True at >= 1024px (Tailwind `lg`). */
export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_MEDIA_QUERY);
}

/** True at < 40rem (Tailwind `sm`) */
export function useIsMobile(): boolean {
  // TODO: rename
  return useMediaQuery(MOBILE_MEDIA_QUERY);
}
